import { Outlet, useNavigate } from 'react-router-dom-v5-compat';
import { ReactNode, Suspense, useCallback, useState } from 'react';

import { OpLayout } from 'new-components/OpLayout/OpLayout';
import { OpContent } from 'new-components/OpContent/OpContent';
import { OpAppHeader } from 'new-components/OpAppHeader/OpAppHeader';
import type { OrgSwitcherProps } from 'new-components/OrgSwitcher/types';
import { OpAppNav } from 'new-components/OpAppNav/OpAppNav';
import { ROUTE_ORG_ID_TEMPLATE } from 'new-components/OpAppScaffold/constants';
import { getOpParams } from 'utils/getOpParams';
import { OpRawMenuItemType } from 'config/createRawMenuItems';
import { OpAppSuspenseLoader } from 'new-components/OpAppScaffold/components/OpAppSuspenseLoader';
import { OpAppBanners } from 'new-components/OpAppBanners/OpAppBanners';

import './OpAppLayout.scss';

interface OpAppLayoutProps {
  homePath: string;
  items: OpRawMenuItemType[];
  title: string;
  orgSwitcherProps?: OrgSwitcherProps;
  banners?: ReactNode;
}

export const OpAppLayout = ({
  homePath,
  items,
  title,
  orgSwitcherProps,
  banners,
}: OpAppLayoutProps) => {
  const { orgId } = getOpParams();
  const navigate = useNavigate();
  const [isShowingNavOnMobile, setIsShowingNavOnMobile] = useState(true);

  const handleLogoClick = useCallback(() => {
    navigate(homePath.replace(ROUTE_ORG_ID_TEMPLATE, orgId?.toString() ?? ''));
  }, [homePath, navigate, orgId]);

  const handleHamburgerClick = useCallback(() => {
    setIsShowingNavOnMobile((prev) => !prev);
  }, [setIsShowingNavOnMobile]);

  return (
    <OpLayout>
      <OpAppBanners banners={banners} />
      <OpLayout className="op-app-layout">
        <OpAppNav
          items={items}
          title={title}
          onLogoClick={handleLogoClick}
          setIsShowingNavOnMobile={setIsShowingNavOnMobile}
          /**
           * Using a prop here so we can use css to hide and show the nav so that when the state still has
           * this hidden, on tablet + it can still be visible
           */
          hidden={!isShowingNavOnMobile}
        />
        <OpLayout>
          <OpAppHeader
            showHamburger={!isShowingNavOnMobile}
            onHamburgerClick={handleHamburgerClick}
            homePath={homePath}
            orgSwitcherProps={orgSwitcherProps}
          />
          <OpContent className="op-app-layout__content">
            <Suspense fallback={<OpAppSuspenseLoader />}>
              <Outlet /> {/* Render content from child routes */}
            </Suspense>
          </OpContent>
        </OpLayout>
      </OpLayout>
    </OpLayout>
  );
};
